import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addChargeDraftHistory,
  editCaseCreation,
  editCaseCreationWithupload,
} from "services/caseServices";
import { CaseCreation, FILE_URL } from "types";
import { errorHandlerOnForm } from "utils/helper";
import ChargeSheetEditor from "../chargeSheet/ChargeSheetEditor";
import { useCourtFilingMarks } from "components/CourtFilingMarks";

type Props = {
  caseData: CaseCreation;
  fetchData: () => void;
  isCoordinator?: boolean;
  isRegistrar?: boolean;
  isProsecutor?: boolean;
  isClerk?: boolean;
  uploadChargeDraft?: File | null;
  setUploadChargeDraft: React.Dispatch<
    React.SetStateAction<File | null | undefined>
  >;
  onRespondBack?: () => void;
};

const ChargeDraftList: FC<Props> = ({
  caseData,

  fetchData,
  isCoordinator = false,
  isRegistrar = false,
  isProsecutor = false,
  isClerk = false,
  uploadChargeDraft,
  setUploadChargeDraft,
  onRespondBack,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<any[]>([]);
  const [finalDecisionDocs, setFinalDecisionDocs] = useState<any[]>([]);
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPreviousDrafts, setShowPreviousDrafts] = useState(false);
  const [showChargeSheet, setShowChargeSheet] = useState(false);
  // The registrar stamps and signs the charge filed to court (as in the
  // discipline system): the office stamp comes from HRMS and the backend
  // marks every page of the PDF.
  const courtMarks = useCourtFilingMarks(
    !!isRegistrar && caseData?.status === "forwarded_to_registrar",
  );

  // Newest charge draft first; the registrar and clerk only see the latest,
  // others can expand the previous ones.
  const sortedDrafts = [...documents].sort(
    (a, b) =>
      new Date(b?.created_at || "").getTime() -
      new Date(a?.created_at || "").getTime(),
  );
  const latestDraft = sortedDrafts[0];
  const previousDrafts = sortedDrafts.slice(1);

  // Whoever can move the case forward from the charge-draft stage:
  //   prosecutor (Pending / Respond Back)  -> Forward to Coordinator
  //   coordinator (forwarded_to_coordinator) -> Forward to Registrar
  const canForward =
    (isProsecutor &&
      (caseData?.status === "Pending" ||
        caseData?.status === "Respond_Back_prosecutor")) ||
    (isCoordinator && caseData?.status === "forwarded_to_coordinator");

  const forwardLabel =
    caseData?.status === "forwarded_to_coordinator"
      ? t("forward_to_registrar")
      : t("forward_to_coordinator");

  // Coordinator can also send the case back to the prosecutor.
  const canRespondBack =
    isCoordinator && caseData?.status === "forwarded_to_coordinator";

  const handleForward = () => {
    if (caseData?.status === "Pending" && !uploadChargeDraft) {
      showTooltip(t("please_upload_charge_draft_file"), "error");
      return;
    }
    setSubmitting(true);
    const api =
      caseData?.status === "Pending" || uploadChargeDraft
        ? editCaseCreationWithupload
        : editCaseCreation;
    api({
      id: caseData?.id,
      status:
        caseData?.status === "forwarded_to_coordinator"
          ? "forwarded_to_registrar"
          : "forwarded_to_coordinator",
      charge_draft_files: uploadChargeDraft || undefined,
    })
      .then(() => {
        if (uploadChargeDraft) {
          addChargeDraftHistory({
            case: caseData?.id,
            file: uploadChargeDraft,
            uploaded_by: loginData?.full_name || "",
          });
        }
        showTooltip(t("successful"), "success");
        setUploadChargeDraft(null);
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_forwarding_case"),
          "error",
        );
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    if (caseData) {
      setDocuments(
        (caseData?.documents?.filter(
          (a) => a.document_type === "upload_charge_draft",
        ) || []) as any,
      );
      setFinalDecisionDocs(
        (caseData?.documents?.filter(
          (a) => a.document_type === "final_court_decision",
        ) || []) as any,
      );
    }
  }, [caseData]);
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("charge_draft")}
          </h3>
          {/* Only prosecutors prepare and sign the charge sheet. */}
          {isProsecutor && (
          <button
            type="button"
            onClick={() => setShowChargeSheet(true)}
            className="rounded-lg border border-red-600 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
          >
            📝 {t("charge_sheet")}
          </button>
          )}
        </div>
        {isProsecutor && showChargeSheet && (
          <ChargeSheetEditor
            caseData={caseData}
            prosecutorName={loginData?.full_name}
            onUseAsDraft={
              isProsecutor &&
              (caseData?.status === "Pending" ||
                caseData?.status === "Respond_Back_prosecutor")
                ? (file) => setUploadChargeDraft(file)
                : undefined
            }
            onClose={() => setShowChargeSheet(false)}
          />
        )}

        {((isRegistrar &&
          caseData?.status === "forwarded_to_registrar" &&
          !caseData?.registration_number) ||
          (isProsecutor &&
            (caseData?.status === "Pending" ||
              caseData?.status === "Respond_Back_prosecutor"))) && (
          <div>
            <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
              {/* Only the registrar may register the registration number. */}
              {isRegistrar && (
                <div className="flex flex-col mt-4 mb-4">
                  <label className=" mb-2">{t("registration_number")}</label>
                  <input
                    required
                    type="text"
                    placeholder="#123456"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    value={registrationNumber ?? ""}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                  />
                </div>
              )}
              <FileUpload
                label={
                  documents.length > 0
                    ? t("re_upload_charge_draft")
                    : t("upload_charge_draft")
                }
                fileTypes="*"
                info={t("upload_files")}
                required={false}
                onChange={(file) => setUploadChargeDraft(file)}
              />

              {courtMarks.fields && (
                <div className="mt-4">{courtMarks.fields}</div>
              )}

              {isRegistrar && caseData?.status === "forwarded_to_registrar" && (
                <div className="mt-4">
                  <button
                    type="button"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                    onClick={() => {
                      if (!registrationNumber) {
                        showTooltip(
                          t("please_enter_registration_number"),
                          "error",
                        );
                        return;
                      }
                      if (
                        caseData?.status === "Pending" &&
                        !uploadChargeDraft
                      ) {
                        showTooltip(
                          t("please_upload_charge_draft_file"),
                          "error",
                        );
                        return;
                      }

                      editCaseCreationWithupload({
                        id: caseData?.id,
                        // The backend stores charge files under charge_draft_files.
                        charge_draft_files: uploadChargeDraft || undefined,
                        ...courtMarks.marks,
                        registration_number: registrationNumber,
                        // Registering the number moves the case to court.
                        status: "sent_to_court",
                      })
                        .then(() => {
                          if (uploadChargeDraft) {
                            addChargeDraftHistory({
                              case: caseData?.id,
                              file: uploadChargeDraft,
                              uploaded_by: loginData?.full_name || "",
                            });
                          }
                          showTooltip(t("successful"), "success");
                          setUploadChargeDraft(null);
                          courtMarks.reset();
                          fetchData();
                        })
                        .catch((error) => {
                          showTooltip(
                            errorHandlerOnForm(error) ||
                              t("error_forwarding_case"),
                            "error",
                          );
                        });
                    }}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {t("save_registration")}
                  </button>
                </div>
              )}
            </div>
            <hr className="mb-4" />
          </div>
        )}

        <div>
          {caseData?.registration_number && (
            <h4 className="text-md mb-2">
              <span className="font-medium text-gray-500">
                {t("registration_number")}:
              </span>{" "}
              <span className="font-bold text-red-600">
                {caseData?.registration_number}
              </span>
            </h4>
          )}
          {documents.length === 0 ? (
            <p className="text-gray-500">{t("no_charge_drafts_found")}</p>
          ) : (
            <div className="space-y-4">
              <DocumentViewerModal
                documents={[latestDraft]}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.CASE_CREATION}`}
                layout="list"
                label={
                  previousDrafts.length > 0
                    ? t("latest_charge_draft")
                    : t("charge_draft_history")
                }
                hideEmptyFile={true}
              />

              {/* Registrar and clerk see only the latest; others can expand previous. */}
              {!isRegistrar && !isClerk && previousDrafts.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowPreviousDrafts((v) => !v)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {t("previous_charge_drafts")} ({previousDrafts.length})
                    </span>
                    <svg
                      className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${
                        showPreviousDrafts ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {showPreviousDrafts && (
                    <div className="px-4 py-4 border-t border-gray-200">
                      <DocumentViewerModal
                        documents={previousDrafts}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.CASE_CREATION}`}
                        layout="list"
                        hideEmptyFile={true}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Available actions for whoever can act, shown below the file. */}
        {(canForward || (canRespondBack && onRespondBack)) && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
              {t("available_actions")}
            </h4>
            {isProsecutor && uploadChargeDraft && (
              <div className="flex items-center gap-2 mb-3 rounded-lg bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
                <span>✓</span>
                <span className="font-medium">{t("charge_draft_ready")}:</span>
                <span className="truncate">{uploadChargeDraft.name}</span>
              </div>
            )}
            <div className="flex flex-row flex-wrap gap-3">
              {canRespondBack && onRespondBack && (
                <button
                  type="button"
                  onClick={onRespondBack}
                  className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 px-4 py-3 bg-white border border-amber-300 text-amber-700 font-medium rounded-lg hover:bg-amber-50 transition-colors"
                >
                  ↩️ {t("respond_back_to_prosecutor")}
                </button>
              )}
              {canForward && (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleForward}
                  className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? t("loading") : forwardLabel}
                </button>
              )}
            </div>
            {isProsecutor && (
              <p className="text-xs text-gray-500 mt-2">
                {t("upload_then_forward_hint")}
              </p>
            )}
          </div>
        )}

        {/* Final court decision document (shown once it exists). */}
        {finalDecisionDocs.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-md font-semibold text-gray-900 mb-2">
              {t("final_decision_document")}
            </h4>
            <DocumentViewerModal
              documents={finalDecisionDocs}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.CASE_CREATION}`}
              layout="list"
              label={t("final_decision_document")}
              hideEmptyFile={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChargeDraftList;
